<script setup lang="ts">
import { computed, useId } from 'vue'

import './base.css'
import EIcon from './EIcon.vue'
import { provideFieldContext } from './field-context'
import { useUiStrings } from '../lib/strings'

/**
 * Label + hint + error wrapper.
 *
 * This is the component that makes a server field error land on the right
 * control: it owns the ids, sets `aria-invalid` and stitches the hint and error
 * text into `aria-describedby`, then publishes all of it through the field
 * context so `EInput`, `ESelect`, `ECheckbox` and friends pick it up without
 * being told. Controls outside the library get the same values as slot props.
 */

const props = withDefaults(
  defineProps<{
    label?: string
    /** Persistent helper text. Announced together with the error, not replaced. */
    hint?: string
    /** One message, or the array a problem+json `errors[]` filter produced. */
    error?: string | string[] | null
    required?: boolean
    /** Renders "optional" beside the label — clearer than an unmarked field. */
    showOptional?: boolean
    disabled?: boolean
    /** Supply when the control manages its own id (e.g. a third-party editor). */
    controlId?: string
    /** Hides the label visually while keeping it for assistive technology. */
    labelHidden?: boolean
  }>(),
  { label: '', hint: '', error: null, controlId: '' },
)

defineSlots<{
  default: (props: {
    id: string
    describedBy: string | undefined
    invalid: boolean
    required: boolean
    disabled: boolean
  }) => unknown
  label: () => unknown
}>()

const strings = useUiStrings()
const generatedId = useId()

const controlId = computed(() => (props.controlId === '' ? generatedId : props.controlId))
const hintId = computed(() => `${controlId.value}-hint`)
const errorId = computed(() => `${controlId.value}-error`)

const messages = computed<string[]>(() => {
  if (props.error === null || props.error === undefined) return []
  const list = Array.isArray(props.error) ? props.error : [props.error]
  return list.filter((message) => typeof message === 'string' && message.trim() !== '')
})

const invalid = computed(() => messages.value.length > 0)

const describedBy = computed<string | undefined>(() => {
  const ids: string[] = []
  if (props.hint !== '') ids.push(hintId.value)
  if (invalid.value) ids.push(errorId.value)
  return ids.length > 0 ? ids.join(' ') : undefined
})

const requiredRef = computed(() => props.required === true)
const disabledRef = computed(() => props.disabled === true)

provideFieldContext({
  controlId,
  describedBy,
  invalid,
  required: requiredRef,
  disabled: disabledRef,
})

</script>

<template>
  <div class="e-field" :class="{ 'e-field--disabled': disabled }">
    <label
      v-if="label !== '' || $slots.label"
      :for="controlId"
      class="e-field__label"
      :class="{ 'e-visually-hidden': labelHidden }"
    >
      <slot name="label">{{ label }}</slot>
      <span v-if="required" class="e-field__required" aria-hidden="true">*</span>
      <span v-if="required" class="e-visually-hidden">{{ strings.fieldRequired }}</span>
      <span v-else-if="showOptional" class="e-field__optional">{{ strings.fieldOptional }}</span>
    </label>

    <slot
      :id="controlId"
      :described-by="describedBy"
      :invalid="invalid"
      :required="requiredRef"
      :disabled="disabledRef"
    />

    <p v-if="hint !== ''" :id="hintId" class="e-field__hint">{{ hint }}</p>

    <!-- role="alert" so a validation failure is announced the moment it lands. -->
    <p v-if="invalid" :id="errorId" class="e-field__error" role="alert">
      <EIcon name="danger" :size="14" />
      <span>{{ messages.join(' ') }}</span>
    </p>
  </div>
</template>
